import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Users, AlertCircle, CheckCircle, Loader2, UserPlus, Trash2, Info } from 'lucide-react';
import api from '../utils/api';

const GovernanceSettings = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    board_enabled: false,
    committees_enabled: false
  });
  const [boardMembers, setBoardMembers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('none');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch governance settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/governance/settings');
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch governance settings:', err);
      setError('Failed to load governance settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch board members
  const fetchBoardMembers = useCallback(async () => {
    if (!settings.board_enabled) return;
    try {
      const response = await api.get('/board/documents/members');
      setBoardMembers(response.data.members || []);
    } catch (err) {
      console.error('Failed to fetch board members:', err);
    }
  }, [settings.board_enabled]);

  // Fetch team members for adding to board
  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await api.get('/team');
      setTeamMembers(response.data.members || []);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchTeamMembers();
  }, [fetchSettings, fetchTeamMembers]);

  useEffect(() => {
    if (settings.board_enabled) {
      fetchBoardMembers();
    }
  }, [settings.board_enabled, fetchBoardMembers]);

  // Toggle board enabled
  const toggleBoard = async () => {
    try {
      setSaving(true);
      setError(null);
      const newValue = !settings.board_enabled;
      await api.put('/governance/settings', { board_enabled: newValue });
      setSettings(prev => ({ ...prev, board_enabled: newValue }));
      setSuccess(newValue ? 'Board of Directors enabled' : 'Board of Directors disabled');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update board setting');
    } finally {
      setSaving(false);
    }
  };

  // Add board member
  const addBoardMember = async () => {
    if (!selectedMember) return;
    try {
      setSaving(true);
      await api.put(`/board/documents/members/${selectedMember}/position`, {
        position: selectedPosition === 'none' ? null : selectedPosition
      });
      setShowAddMemberModal(false);
      setSelectedMember('');
      setSelectedPosition('none');
      fetchBoardMembers();
      setSuccess('Board member added');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add board member');
    } finally {
      setSaving(false);
    }
  };

  // Remove board member
  const removeBoardMember = async (userId) => {
    if (!window.confirm('Remove this user from the board?')) return;
    try {
      await api.delete(`/governance/board/members/${userId}`);
      fetchBoardMembers();
      setSuccess('Board member removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to remove board member');
    }
  };

  // Committees are managed in Governance > Settings

  // Get non-board members for adding
  const availableMembers = teamMembers.filter(
    m => !boardMembers.some(bm => bm.id === m.user?.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600">Loading governance settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Shield className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Governance</h2>
            <p className="text-sm text-gray-500">
              Manage Board of Directors settings
            </p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          {success}
        </div>
      )}

      {/* Feature Toggles */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {/* Board of Directors Toggle */}
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Board of Directors</h3>
                <p className="text-sm text-gray-500">
                  Enable board management, meetings, minutes, and resolutions
                </p>
              </div>
            </div>
            <button
              onClick={toggleBoard}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.board_enabled ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings.board_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          
          {/* Info box */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">When enabled:</p>
              <ul className="mt-1 list-disc list-inside space-y-1 text-blue-700">
                <li>Users with <strong>Board Member</strong> role automatically get board access</li>
                <li>You can grant board access to other team members below</li>
                <li>Governance navigation appears for users with board access</li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      {/* Board Members Section (shown when board is enabled) */}
      {settings.board_enabled && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Board Members</h3>
                <p className="text-sm text-gray-500">
                  Users with board access (includes Board Member role + explicitly added)
                </p>
              </div>
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                Add Member
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {boardMembers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No board members yet.</p>
                <p className="text-sm">Assign users the "Board Member" role or add them explicitly above.</p>
              </div>
            ) : (
              boardMembers.map(member => (
                <div key={member.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    {member.profile_image_url ? (
                      <img 
                        src={member.profile_image_url} 
                        alt={member.name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-medium">
                          {member.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{member.name}</span>
                          {member.board_member_id ? (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                              Granted Access
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                              Admin/Owner Access
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">{member.email}</span>
                        {member.position && (
                          <p className="text-xs text-purple-600 font-medium mt-1">
                            {member.position.replace(/_/g, ' ')}
                          </p>
                        )}
                      </div>
                    </div>
                  {member.board_member_id && (
                    <button
                      onClick={() => removeBoardMember(member.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove board access"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}


      {/* Add Board Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add Board Member</h3>
            <p className="text-sm text-gray-500 mb-4">
              Grant board access to a team member. Users with the "Board Member" role already have access.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Team Member
                </label>
                <select
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Choose a member...</option>
                  {availableMembers.map(m => (
                    <option key={m.user?.id} value={m.user?.id}>
                      {m.user?.name} ({m.role})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role (optional)
                </label>
                <select
                  value={selectedPosition}
                  onChange={(e) => setSelectedPosition(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="none">No position</option>
                  <option value="chair">Chair</option>
                  <option value="vice_chair">Vice Chair</option>
                  <option value="secretary">Secretary</option>
                  <option value="treasurer">Treasurer</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedMember('');
                  setSelectedPosition('none');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={addBoardMember}
                disabled={!selectedMember || saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add to Board'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GovernanceSettings;
